import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, Target, Activity, Loader2, Dumbbell, Users, Calendar } from 'lucide-react';
import { drawField, drawElement, boardViewport, normalizeBoardElements } from '../lib/boardDrawing';
import { API_URL } from '../lib/apiUrl';

// Paleta fija (esmeralda oscuro) para que se vea bien aunque el visitante
// no tenga sesión ni tema configurado.
const C = {
  bg: '#0A0F0C', surface: '#131A15', surface2: '#1A231C',
  text0: '#F0F2F5', text1: '#C3CBC6', text2: '#8A948E',
  primary: '#10B981', line: 'rgba(255,255,255,0.08)',
};

const INTENSITY = {
  baja: '#4dd6ff', media: '#ffd34d', 'media-alta': '#ff8c00',
  alta: '#ff4d6d', 'muy-alta': '#ff0055',
};

function safeDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
}

/** Pizarra táctica de una tarea. Mismo dibujado que dentro de la app. */
function MiniField({ elements, orientation = 'half-vertical' }) {
  const canvasRef = useRef(null);
  // Mismo criterio que dentro de la app: las pizarras de sesiones Elite se
  // guardan en 700x460 con el campo entero y hay que pintarlas así.
  const view = boardViewport(elements, orientation);
  const dims = view.dims;
  const normalized = normalizeBoardElements(elements);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    drawField(ctx, dims.width, dims.height, view.orientation);
    normalized.forEach(el => drawElement(ctx, el));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized, dims.width, dims.height, view.orientation]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      width={dims.width}
      height={dims.height}
      style={{ width: '100%', height: 'auto', aspectRatio: `${dims.width} / ${dims.height}`, borderRadius: 10, display: 'block' }}
    />
  );
}

export default function PublicSessionPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/sessions/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const wrap = { minHeight: '100vh', background: C.bg, color: C.text0, fontFamily: 'Inter, system-ui, sans-serif' };

  if (loading) {
    return (
      <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: C.text2 }}>
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando sesión...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ ...wrap, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
        <img src="/micoach-logo-blanco.svg" alt="MiCoach" style={{ height: 34, opacity: 0.9 }} />
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>Enlace no disponible</h1>
        <p style={{ color: C.text2, maxWidth: 360 }}>Este enlace no es válido o el entrenador ha dejado de compartir la sesión.</p>
      </div>
    );
  }

  const chip = (bg, color, children) => (
    <span style={{ fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 8, background: bg, color }}>{children}</span>
  );

  const label = (text, color = C.primary) => (
    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color }}>{text}</p>
  );

  const blocks = Array.isArray(data.blocks) ? data.blocks : [];
  const hasSchedule = blocks.some(b => b.start_time);

  return (
    <div style={wrap}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 60px' }}>
        {/* Marca */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <img src="/micoach-logo-blanco.svg" alt="MiCoach" style={{ height: 24 }} />
          <span style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>MiCoach</span>
        </div>

        {/* Cabecera de la sesión */}
        <p style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.primary, fontWeight: 600 }}>
          Sesión de entrenamiento
        </p>
        <h1 style={{ fontSize: 'clamp(1.6rem, 5vw, 2.4rem)', fontWeight: 800, letterSpacing: '-0.02em', marginTop: 6, lineHeight: 1.1 }}>
          {data.context || 'Sesión de entrenamiento'}
        </h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          {data.is_elite && chip('rgba(212,175,55,0.15)', '#D4AF37', 'ELITE')}
          {data.team_name && chip(C.surface2, C.text1, data.team_name)}
          {data.date && chip(C.surface2, C.text1, safeDate(data.date))}
          {data.day && chip(C.surface2, C.text1, data.day)}
          {data.time_start && chip('rgba(16,185,129,0.12)', C.primary, `${data.time_start} h`)}
          {data.total_time ? chip('rgba(16,185,129,0.12)', C.primary, `${data.total_time} min`) : null}
          {data.field && chip(C.surface2, C.text1, data.field)}
          {data.players_count ? chip(C.surface2, C.text1, `${data.players_count} jugadores`) : null}
          {data.intensity && chip(`${INTENSITY[data.intensity] || C.primary}20`, INTENSITY[data.intensity] || C.primary, `Intensidad ${data.intensity}`)}
        </div>

        {/* Objetivos */}
        {Array.isArray(data.objectives) && data.objectives.filter(Boolean).length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: C.primary }}>
              <Target className="w-4 h-4" />{label('Objetivos')}
            </div>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.objectives.filter(Boolean).map((o, i) => (
                <li key={i} style={{ display: 'flex', gap: 8, color: C.text1, fontSize: 14 }}>
                  <span style={{ color: C.primary }}>›</span>{o}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Focos */}
        {(data.focus_tactical || data.focus_physical || data.focus_technical) && (
          <div style={{ marginTop: 24, display: 'grid', gap: 10 }}>
            {[['Táctico', data.focus_tactical], ['Físico', data.focus_physical], ['Técnico', data.focus_technical]]
              .filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 12, padding: '12px 14px' }}>
                  <p style={{ fontSize: 11, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Foco {k}</p>
                  <p style={{ fontSize: 14, color: C.text0 }}>{v}</p>
                </div>
              ))}
          </div>
        )}

        {/* Cronograma: resumen de horas por tarea */}
        {hasSchedule && blocks.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: C.primary }}>
              <Calendar className="w-4 h-4" />{label('Cronograma')}
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden' }}>
              {blocks.map((b, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderTop: i ? `1px solid ${C.line}` : 'none',
                }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: C.primary, minWidth: 46 }}>
                    {b.start_time || '--:--'}
                  </span>
                  <span style={{ fontSize: 13.5, color: C.text1, flex: 1 }}>{b.name}</span>
                  {b.duration ? <span style={{ fontSize: 12, color: C.text2 }}>{b.duration}′</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tareas con pizarra */}
        {blocks.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: C.primary }}>
              <Activity className="w-4 h-4" />{label(`Tareas (${blocks.length})`)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {blocks.map((b, i) => {
                const ic = INTENSITY[b.intensity] || C.primary;
                const steps = b.board?.steps || [];
                return (
                  <div key={i} style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 14, padding: 16 }}>
                    {/* Cabecera de la tarea */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(16,185,129,0.12)', color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>
                          {b.isGym ? <><Dumbbell className="w-3.5 h-3.5 inline mr-1" style={{ color: ic }} />{b.name}</> : b.name}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                        {b.start_time && (
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.primary }}>{b.start_time}</span>
                        )}
                        {b.duration ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.text2 }}>
                            <Clock className="w-3.5 h-3.5" />{b.duration}′
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {b.objective && <p style={{ fontSize: 13, color: ic, marginTop: 8, fontWeight: 600 }}>{b.objective}</p>}
                    {b.description && <p style={{ fontSize: 13.5, color: C.text1, marginTop: 6, lineHeight: 1.55 }}>{b.description}</p>}

                    {/* Pizarra táctica (todos los pasos guardados) */}
                    {steps.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <p style={{ fontSize: 11, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                          Pizarra{steps.length > 1 ? ` · ${steps.length} pasos` : ''}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: steps.length > 1 ? 'repeat(auto-fit, minmax(160px, 1fr))' : '1fr', gap: 8 }}>
                          {steps.map((els, si) => (
                            <div key={si} style={{ background: '#0a1a10', borderRadius: 10, padding: 4 }}>
                              <MiniField elements={els} orientation={b.board.orientation} />
                              {steps.length > 1 && (
                                <p style={{ fontSize: 10, color: C.text2, textAlign: 'center', marginTop: 2 }}>Paso {si + 1}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Equipos (sesiones Elite) */}
                    {Array.isArray(b.teams) && b.teams.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <p style={{ fontSize: 11, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                          <Users className="w-3 h-3 inline mr-1" />Equipos
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                          {b.teams.map((t, ti) => (
                            <div key={ti} style={{ background: C.surface2, borderRadius: 10, padding: '8px 10px' }}>
                              <p style={{ fontSize: 11, fontWeight: 700, color: C.primary, marginBottom: 4 }}>{t.name}</p>
                              <p style={{ fontSize: 12, color: C.text1, lineHeight: 1.5 }}>{t.players.join(' · ')}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bloques de gimnasio */}
                    {Array.isArray(b.gymBlocks) && b.gymBlocks.length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {b.gymBlocks.map((g, gi) => (
                          <div key={gi} style={{ background: C.surface2, borderRadius: 10, padding: '8px 10px' }}>
                            <p style={{ fontSize: 12, fontWeight: 700, color: C.text0 }}>{g.name}</p>
                            <p style={{ fontSize: 12, color: C.text2, marginTop: 2 }}>
                              {(g.exercises || []).map(e => `${e.name} ${e.series}x${e.reps}`).filter(s => s.trim()).join(' · ')}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Puntos de coaching */}
                    {b.coaching_points && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.line}` }}>
                        <p style={{ fontSize: 11, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Puntos de coaching</p>
                        <p style={{ fontSize: 13, color: C.text1, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{b.coaching_points}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sin tareas: avisamos en lugar de dejar la página vacía */}
        {blocks.length === 0 && (
          <div style={{ marginTop: 28, padding: 18, borderRadius: 12, background: C.surface, border: `1px solid ${C.line}`, textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: C.text2 }}>Esta sesión todavía no tiene tareas registradas.</p>
          </div>
        )}

        {/* Observaciones / resumen */}
        {(data.summary_text || data.observations) && (
          <div style={{ marginTop: 24, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.20)', borderRadius: 12, padding: 16 }}>
            <p style={{ fontSize: 11, color: C.primary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              {data.observations ? 'Observaciones' : 'Resumen'}
            </p>
            <p style={{ fontSize: 14, color: C.text1, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {data.summary_text || data.observations}
            </p>
          </div>
        )}

        {/* Pie con CTA suave */}
        <div style={{ marginTop: 40, paddingTop: 20, borderTop: `1px solid ${C.line}`, textAlign: 'center' }}>
          <a href="/" style={{ fontSize: 13, color: C.text2, textDecoration: 'none' }}>
            Creado con <span style={{ color: C.primary, fontWeight: 700 }}>MiCoach</span> · la plataforma del entrenador
          </a>
        </div>
      </div>
    </div>
  );
}
